import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { SSO_PROVIDERS, type SsoPortal } from '@gokrew/models/sso';
import { cn } from '@/lib/utils';
import { ssoStartUrl, useSsoProviders } from '../sso';
import { ProviderLogo } from './ProviderLogo';
import styles from './SsoButtons.module.css';

export interface SsoButtonsProps {
  portal: SsoPortal;
  /** Relative path to open after sign-in (the login page's `redirect`). */
  returnTo?: string | null;
  className?: string;
}

/**
 * "Continue with Microsoft / Google" on the company and employee login pages
 * (D199), then an "or" divider before the password form. Only the providers
 * this environment has credentials for are shown; none, and it renders
 * nothing. Each button is a plain link to the backend's start route.
 */
export function SsoButtons({ portal, returnTo, className }: SsoButtonsProps) {
  const { t } = useTranslation(TranslationNS.Sso);
  const { data } = useSsoProviders();
  const providers = SSO_PROVIDERS.filter((provider) => data?.[provider]);
  if (providers.length === 0) return null;

  return (
    <div className={cn(styles.root, className)}>
      <div className={styles.buttons}>
        {providers.map((provider) => (
          <a
            key={provider}
            href={ssoStartUrl(provider, portal, returnTo)}
            className={styles.button}
          >
            <ProviderLogo provider={provider} />
            <span>{t(`continue_${provider}`)}</span>
          </a>
        ))}
      </div>
      <div className={styles.divider} role="separator">
        <span className={styles.dividerLabel}>{t('or')}</span>
      </div>
    </div>
  );
}
